// SoundManager.js

// 1. Pre-load all sound effects
const sounds = {
  pop: new Audio('/sounds/pop.mp3'),
  laser: new Audio('/sounds/laser.mp3'),
  explosion: new Audio('/sounds/explosion.mp3'),
  engine: new Audio('/sounds/engine_rev.mp3'),
  screech: new Audio('/sounds/screech.mp3'),
  zap: new Audio('/sounds/zap.mp3'),
  slice: new Audio('/sounds/slice.mp3'),
  piano: new Audio('/sounds/piano.mp3'),
  pianoMiss: new Audio('/sounds/piano_miss.mp3'),
  bridge: new Audio('/sounds/wood_thud.mp3'),
  fall: new Audio('/sounds/fall.mp3'),
  grunt: new Audio('/sounds/grunt.mp3'),
  monster: new Audio('/sounds/monster_roar.mp3'),
  error: new Audio('/sounds/error_buzzer.mp3')
};

// 🎛️ 2. THE MIXING BOARD
const volumes = {
  pop: 0.6,
  laser: 0.2,
  explosion: 0.4,
  engine: 0.4,
  screech: 0.3,
  zap: 0.5,
  slice: 0.4,
  piano: 0.6,
  pianoMiss: 0.5,
  bridge: 0.8,
  fall: 0.4,
  grunt: 0.5,
  monster: 0.7,
  error: 0.3
};

// Apply the custom volumes to each sound
Object.keys(sounds).forEach(key => {
  if (sounds[key]) {
    sounds[key].volume = volumes[key] || 0.5;
  }
});

// 3. The Play Function
export const playSound = (soundName, isSoundEnabled) => {
  if (!isSoundEnabled) return; 

  const audio = sounds[soundName];
  if (audio) {
    const clonedSound = audio.cloneNode();
    clonedSound.volume = audio.volume; // Tells the clone to use your custom volume!
    clonedSound.play().catch(e => console.log("Audio blocked by browser:", e));
  }
};

// ==========================================
// ⌨️ TYPING SYNTHESIZER (Web Audio API)
// ==========================================
let audioCtx = null;
let masterGain = null;
let noiseBuffer = null;

export const initAudio = () => {
  if (audioCtx) return;
  
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  audioCtx = new AudioContext();
  
  masterGain = audioCtx.createGain();
  masterGain.gain.value = 0.5; // Master volume
  masterGain.connect(audioCtx.destination);

  // Pre-compute white noise for mechanical/typewriter friction sounds
  const bufferSize = audioCtx.sampleRate * 0.5; 
  noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    output[i] = Math.random() * 2 - 1;
  }

  if (audioCtx.state === 'suspended') {
     audioCtx.resume();
  }
};

export const playKeystroke = (keyType = 'standard') => {
  if (!audioCtx) return;
  
  // Read the active profile from local storage instantly
  const profile = localStorage.getItem('soundProfile') || 'modern';
  const t = audioCtx.currentTime;

  // 1. ERROR BZZZT (Universal)
  if (keyType === 'error') {
     const osc = audioCtx.createOscillator();
     const env = audioCtx.createGain();
     osc.type = 'sawtooth';
     osc.frequency.setValueAtTime(150, t);
     env.gain.setValueAtTime(0.1, t);
     env.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
     osc.connect(env);
     env.connect(masterGain);
     osc.start(t);
     osc.stop(t + 0.2);
     return;
  }

  // 2. VINTAGE TYPEWRITER
  if (profile === 'typewriter') {
      if (keyType === 'enter') {
          // The Classic Typewriter "DING!"
          const osc1 = audioCtx.createOscillator();
          const osc2 = audioCtx.createOscillator();
          const env = audioCtx.createGain();
          osc1.type = 'sine'; osc2.type = 'sine';
          osc1.frequency.setValueAtTime(1200, t);
          osc2.frequency.setValueAtTime(2000, t);
          env.gain.setValueAtTime(0.4, t);
          env.gain.exponentialRampToValueAtTime(0.001, t + 1.0);
          osc1.connect(env); osc2.connect(env);
          env.connect(masterGain);
          osc1.start(t); osc2.start(t);
          osc1.stop(t + 1.0); osc2.stop(t + 1.0);
      } else {
          // Typewriter Metallic Clack
          const osc = audioCtx.createOscillator();
          const oscEnv = audioCtx.createGain();
          osc.type = 'square';
          osc.frequency.setValueAtTime(200, t);
          osc.frequency.exponentialRampToValueAtTime(50, t + 0.05);
          oscEnv.gain.setValueAtTime(0.1, t);
          oscEnv.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
          osc.connect(oscEnv); oscEnv.connect(masterGain);
          osc.start(t); osc.stop(t + 0.05);

          const noise = audioCtx.createBufferSource();
          noise.buffer = noiseBuffer;
          const noiseEnv = audioCtx.createGain();
          const filter = audioCtx.createBiquadFilter();
          filter.type = 'bandpass'; filter.frequency.value = 1000;
          noiseEnv.gain.setValueAtTime(0.3, t);
          noiseEnv.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
          noise.connect(filter); filter.connect(noiseEnv); noiseEnv.connect(masterGain);
          noise.start(t);
      }
  } 
  
  // 3. MECHANICAL KEYBOARD
  else if (profile === 'mechanical') {
      // Plastic Snap
      const noise = audioCtx.createBufferSource();
      noise.buffer = noiseBuffer;
      const noiseEnv = audioCtx.createGain();
      const filter = audioCtx.createBiquadFilter();
      filter.type = 'highpass'; filter.frequency.value = 2500;
      noiseEnv.gain.setValueAtTime(0.15, t);
      noiseEnv.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
      noise.connect(filter); filter.connect(noiseEnv); noiseEnv.connect(masterGain);
      noise.start(t);

      // Bottom-out thud
      const osc = audioCtx.createOscillator();
      const oscEnv = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(300, t);
      oscEnv.gain.setValueAtTime(0.08, t);
      oscEnv.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
      osc.connect(oscEnv); oscEnv.connect(masterGain);
      osc.start(t); osc.stop(t + 0.03);
  } 
  
  // 4. MODERN SINE POP (Default)
  else {
      const osc = audioCtx.createOscillator();
      const env = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(300, t + 0.05);
      env.gain.setValueAtTime(0.1, t);
      env.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
      osc.connect(env); env.connect(masterGain);
      osc.start(t); osc.stop(t + 0.05);
  }
}